import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import {
  IPowerSlide,
  resolvePowerSlideTheme,
} from "../../../models/interactions/PowerSlideHelper";
import { PowerSlideSurface } from "../../../models/interactions/PowerSlideTemplates";
import {
  availableInks,
  canSetPowerSlideInkColor,
  canSetPowerSlideSurfaceBackground,
  inkColor,
  POWER_SLIDE_PRESETS,
  PowerSlideInk,
  PowerSlideTheme,
  setPowerSlideAccent,
  setPowerSlideInkColor,
  setPowerSlideSurfaceBackground,
  setPowerSlideSurfaceInk,
} from "../../../models/interactions/PowerSlideTheme";
import PowerSlideColorField from "./PowerSlideColorField";
import PowerSlidePopover from "./PowerSlidePopover";

/** The three surface bands of a theme, as shown in the preset field. */
function MiniSwatch({ theme }: { theme: PowerSlideTheme }) {
  return (
    <span className="flex h-[20px] w-[46px] overflow-hidden rounded-[6px] border border-solid border-black/[0.14]">
      {(["content", "title", "section"] as PowerSlideSurface[]).map((role) => (
        <span
          key={role}
          className="flex-1"
          style={{ backgroundColor: theme.surfaces[role].background }}
        />
      ))}
    </span>
  );
}

/**
 * The light/dark ink choice, rendered as `Aa` on the surface's own colour so
 * the decision is made by looking. Only legible options are offered - a
 * contrast error message in a theming flow would mean the model was wrong.
 */
function InkChoice({
  theme,
  role,
  onChange,
}: {
  theme: PowerSlideTheme;
  role: PowerSlideSurface;
  onChange: (ink: PowerSlideInk) => void;
}) {
  const { t } = useTranslation();
  const legal = availableInks(theme, role);
  const background = theme.surfaces[role].background;

  return (
    <div className="flex w-full items-center gap-[8px]">
      <span className="flex-1 text-[9.5px] text-creator-text-soft">
        {t("powerslide.theme.text", { defaultValue: "Text" })}
      </span>
      {(["dark", "light"] as PowerSlideInk[]).map((ink) => {
        const selected = theme.surfaces[role].ink === ink;
        const allowed = legal.includes(ink);
        return (
          <button
            key={ink}
            type="button"
            aria-label={`powerslide-ink-${role}-${ink}`}
            aria-pressed={selected}
            disabled={!allowed}
            onClick={() => onChange(ink)}
            className={classNames(
              "flex h-[26px] w-[40px] items-center justify-center rounded-[6px] border-solid text-[12.5px] font-medium",
              selected
                ? "border-2 border-creator-primary"
                : "border border-creator-border",
              // Not hidden: seeing the option greyed explains why it is not
              // available far better than it silently disappearing.
              !allowed && "cursor-not-allowed opacity-30"
            )}
            style={{ backgroundColor: background, color: inkColor(theme, ink) }}
          >
            Aa
          </button>
        );
      })}
    </div>
  );
}

export default function PowerSlideThemeEditor() {
  const deck = useSelector(selectSelectedElement) as IPowerSlide | undefined;
  const theme = resolvePowerSlideTheme(deck);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isPresetOpen, setPresetOpen] = useState(false);
  const presetAnchor = useRef<HTMLButtonElement>(null);

  /**
   * The theme belongs to the deck, so a change is an edit to this interaction -
   * which also makes it undoable and dirty-marking like any other slide edit.
   */
  const apply = (next: PowerSlideTheme) => {
    if (!deck) return;
    const updated: IPowerSlide = { ...deck, theme: next };
    dispatch(updateSelectedInteraction(updated));
  };
  const presetName = (presetId: string) =>
    t(`powerslide.theme.presets.${presetId}`, {
      defaultValue:
        presetId === "custom"
          ? t("powerslide.theme.customTheme", { defaultValue: "Custom" })
          : presetId,
    });

  const surfaceRow = (role: PowerSlideSurface, label: string) => (
    <div className="flex w-full flex-col gap-[6px]">
      <PowerSlideColorField
        label={label}
        value={theme.surfaces[role].background}
        ariaLabel={`powerslide-surface-${role}`}
        isUsable={(color) =>
          canSetPowerSlideSurfaceBackground(theme, role, color)
        }
        onChange={(color) =>
          apply(setPowerSlideSurfaceBackground(theme, role, color))
        }
      />
      <InkChoice
        theme={theme}
        role={role}
        onChange={(ink) => apply(setPowerSlideSurfaceInk(theme, role, ink))}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-[14px]">
      {/* No scope note: the panel sits in this deck's properties column and
          the theme is this deck's, so its placement is the explanation. */}
      <div className="flex w-full flex-col gap-[6px]">
        <span className="text-[13px] font-medium text-creator-text-sub">
          {t("powerslide.theme.preset", { defaultValue: "Preset" })}
        </span>
        <button
          type="button"
          ref={presetAnchor}
          aria-label="powerslide-theme-preset"
          aria-expanded={isPresetOpen}
          onClick={() => setPresetOpen(!isPresetOpen)}
          className="flex w-full items-center gap-[8px] rounded-[7px] border border-solid border-creator-border py-[6px] pl-[6px] pr-[9px] hover:border-creator-primary"
        >
          <MiniSwatch theme={theme} />
          <span className="flex-1 truncate text-left text-[12.5px] font-medium text-creator-text">
            {presetName(theme.presetId)}
          </span>
          <KeyboardArrowDownIcon
            sx={{ fontSize: 14 }}
            className="text-creator-text-sub"
          />
        </button>
        <PowerSlidePopover
          anchorRef={presetAnchor}
          isOpen={isPresetOpen}
          onClose={() => setPresetOpen(false)}
          role="listbox"
          matchAnchorWidth
          ariaLabel="powerslide-theme-presets"
        >
          <div className="flex flex-col gap-[2px] p-[6px]">
            {POWER_SLIDE_PRESETS.map((preset) => (
              <button
                key={preset.presetId}
                type="button"
                role="option"
                aria-selected={preset.presetId === theme.presetId}
                aria-label={`powerslide-theme-${preset.presetId}`}
                onClick={() => {
                  apply(preset);
                  setPresetOpen(false);
                }}
                className="flex items-center gap-[8px] rounded-[6px] px-[4px] py-[4px] text-left hover:bg-creator-childHover"
              >
                <MiniSwatch theme={preset} />
                <span className="flex-1 text-[12.5px] text-creator-text">
                  {presetName(preset.presetId)}
                </span>
              </button>
            ))}
          </div>
        </PowerSlidePopover>
      </div>

      {surfaceRow(
        "content",
        t("powerslide.theme.contentSlides", { defaultValue: "Content slides" })
      )}
      {surfaceRow(
        "title",
        t("powerslide.theme.titleSlides", { defaultValue: "Title slides" })
      )}
      {surfaceRow(
        "section",
        t("powerslide.theme.sectionSlides", { defaultValue: "Section slides" })
      )}

      {/* Accent is not a surface: it is rules, dots, numerals and stat figures,
          so it is set on its own and has no text choice. */}
      <PowerSlideColorField
        label={t("powerslide.theme.accent", { defaultValue: "Accent" })}
        value={theme.accent}
        ariaLabel="powerslide-accent"
        onChange={(color) => apply(setPowerSlideAccent(theme, color))}
      />

      <div className="h-px w-full bg-creator-border" />

      <PowerSlideColorField
        label={t("powerslide.theme.lightText", { defaultValue: "Light text" })}
        value={theme.inkLight}
        ariaLabel="powerslide-ink-light"
        isUsable={(color) => canSetPowerSlideInkColor(theme, "light", color)}
        onChange={(color) => apply(setPowerSlideInkColor(theme, "light", color))}
      />
      <PowerSlideColorField
        label={t("powerslide.theme.darkText", { defaultValue: "Dark text" })}
        value={theme.inkDark}
        ariaLabel="powerslide-ink-dark"
        isUsable={(color) => canSetPowerSlideInkColor(theme, "dark", color)}
        onChange={(color) => apply(setPowerSlideInkColor(theme, "dark", color))}
      />
    </div>
  );
}
